import { Tooltip } from '@mui/material'
import { Error, Info } from '@mui/icons-material'
import React from 'react'
import Colors from 'utils/Colors'

import { BGC_FIELDS } from 'utils/constants/bgcFields'
import { buildRiskColor, buildRiskMessage, buildScore } from 'utils/functions'

import { showWaitingIcon } from 'components/CommonIcon'
import _ from 'lodash'
import { reportError } from 'utils/errorReporter'
import styles from '../styles'

const BuildScoreBoard = (props) => {
  const classes = styles()
  const { scoreBoardData, bgcData, isInDenyList } = props

  if (scoreBoardData?.score_data?.compliance_list) {
    const { reason, value } = scoreBoardData?.score_data?.compliance_list

    scoreBoardData.score_data.compliance_list.reason =
      value > 0 ? `Encontra-se na "${reason}"` : reason
  }

  const scoreColor = ['HIGH_PEP', 'HIGH_ATTENTION_LIST'].includes(
    scoreBoardData.risk_level,
  )
    ? { color: Colors.textColor }
    : buildScore(scoreBoardData.final_score)

  const scoreTitle = (type) => {
    switch (type) {
      case BGC_FIELDS.job_role:
        return 'Cargo'
      case BGC_FIELDS.occupation_area:
        return 'Área de Ocupação'
      case BGC_FIELDS.negative_media:
        return 'Mídia Negativa'
      case BGC_FIELDS.pep:
        return 'PEP'
      case BGC_FIELDS.restriction_data:
        return 'Listas Restritivas'
      case BGC_FIELDS.process_data:
        return 'Processos Judiciais'
      case BGC_FIELDS.family_relation_data:
        return 'Vínculos Familiares'
      case BGC_FIELDS.company_data:
        return 'Participações Societárias'
      case BGC_FIELDS.compliance_list:
        return 'Compliance List'
      default:
        return 'N/A'
    }
  }

  const showPointsCell = (field) => (
    <React.Fragment>
      {scoreBoardData.score_data[field].value}

      {scoreBoardData.score_data[field].value < 1 &&
      scoreBoardData.score_data[field].value !== 0
        ? ' ponto'
        : ' pontos'}
    </React.Fragment>
  )

  const showReasonCell = (fieldData) => (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
      }}
    >
      <span>{fieldData.reason ? fieldData.reason : 'N/A'}</span>
      {fieldData.warnings ? (
        <span>
          <Tooltip title={`Dado inconsistente: ${fieldData.warnings[0].msg}`}>
            <Error style={{ color: Colors.yellow }} fontSize="small" />
          </Tooltip>
        </span>
      ) : null}
    </span>
  )

  const showFinalScore = () => (
    <React.Fragment>
      <span>Score Final: </span>

      {scoreBoardData.is_complete ? (
        <span
          style={{
            fontWeight: 600,
            color: scoreColor.color,
          }}
        >
          {`
        ${scoreBoardData.final_score.toFixed(1)} 
        ${
          scoreBoardData.final_score < 1 && scoreBoardData.final_score !== 0
            ? ' ponto'
            : ' pontos'
        }
      `}
        </span>
      ) : (
        showWaitingIcon({ fontSize: 'small' })
      )}
    </React.Fragment>
  )

  const showRisk = () => {
    return (
      scoreBoardData.is_complete && (
        <React.Fragment>
          <span>Risco: </span>

          <span
            style={{
              color: buildRiskColor(scoreBoardData.risk_level),
            }}
          >
            {buildRiskMessage(scoreBoardData.risk_level)}
          </span>
        </React.Fragment>
      )
    )
  }

  const showResult = () => {
    const isApproved = bgcData?.result === 'APPROVED' && scoreBoardData.result === 'APPROVED'
    const isWaitingReview = bgcData?.result === 'WAITING_FOR_REVIEW' || scoreBoardData.result === 'WAITING_FOR_REVIEW'
    const hasReviews = !_.isEmpty(bgcData?.reviews)

    const getResult = () => {
      if (bgcData?.result == null) {
        return null
      }
      if (isWaitingReview) {
        return 'Revisão manual requerida'
      }
      if (hasReviews && !isApproved) {
        return 'Reprovado(a) após revisão manual'
      }
      if (hasReviews && isApproved) {
        return 'Aprovado(a) após revisão manual'
      }
      return `${!isApproved ? 'Reprovado(a)' : 'Aprovado(a)'} automaticamente`
  
    }
    const result = getResult()

    return (
      result && (
        <React.Fragment>
          <span>Situação: </span>
          <b
            style={{
              color: isApproved ? Colors.green : (isWaitingReview ? Colors.orange : Colors.red),
            }}
          >
            {result}
          </b>
        </React.Fragment>
      )
    )
  }

  const showDenyListMessage = () => (
    <div
      style={{
        fontWeight: 600,
        color: scoreColor.color,
      }}
    >
      Reprovado. Candidato encontra-se na &quot;Deny List&quot;.
    </div>
  )

  const showScoreResume = () => (
    <React.Fragment>
      <div>{showFinalScore()}</div>
      <div>{showRisk()}</div>
      <div>{showResult()}</div>
    </React.Fragment>
  )

  try {
    return (
      <div className={classes.scoreboardContainer}>
        <div className={classes.scoreResume}>
          {isInDenyList ? showDenyListMessage() : showScoreResume()}
        </div>
        {!isInDenyList && (
          <table className={classes.dataTable}>
            <thead>
              <tr>
                <th>Parâmetro</th>
                <th>
                  <span>
                    <span>Pontuação</span>
                    <span>
                      <Tooltip title="Quanto maior o score, maior o risco">
                        <Info fontSize="small" style={{ color: '#AAA' }} />
                      </Tooltip>
                    </span>
                  </span>
                </th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {scoreBoardData &&
                Object.keys(BGC_FIELDS).map((field) => {
                  const fieldData = scoreBoardData.score_data[field]
                  return (
                    fieldData && (
                      <tr key={`tr:${field}`}>
                        <td>{scoreTitle(field)}</td>
                        <td>{showPointsCell(field)}</td>
                        <td>{showReasonCell(fieldData)}</td>
                      </tr>
                    )
                  )
                })}
            </tbody>
          </table>
        )}
      </div>
    )
  } catch (err) {
    reportError(err)
    return (
      <tbody>
        <tr className={classes.errorCaptured}>
          <td colSpan="20" style={{ padding: 10 }}>
            Ocorreu um erro ao tratar os dados
          </td>
        </tr>
      </tbody>
    )
  }
}

export default BuildScoreBoard
